import debounce from 'lodash/debounce';
import isUndefined from 'lodash/isUndefined';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import {
    IEditorProps,
} from '@src/slide/domain/ui-model/element-modeles/property/text/interface/editor-props.interface';
import { getPlaceHolderType, judgeTextIsEmpty, isTextSomeAsDefault } from '@editor/src/page/index/components/canvas/element-types/shape-element/utils';
import { getTextRect } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-rect';
import { IReactionDisposer, reaction, transaction as mobxTransaction, when } from 'mobx';
import { ITextEditor } from '@src/slide/domain/ui-model/element-modeles/property/text/interface/text-editor.interface';
import { isEmptyText } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/utils/text-util';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import { TDWActionType, TDWModuleType, TDWOperationType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { transaction } from '@src/slide/domain/slide-app';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import TextAdjustManager from '@src/slide/domain/ui-model/element-modeles/property/text/text-adjust';
import { getLogicPropertyKeyMap, isVerticalDirection } from '@src/slide/domain/ui-model/element-modeles/property/text/utils/text-direction';
import { PlaceHolderMode } from '@src/slide/domain/ui-model/element-modeles/property/placeholder/PlaceHolderMode';
import { CommonEditLogicWithTextEditor } from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/common-edit-logic-with-text-editor';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import { MOUSE_EVENT_KEY } from '@utils/event/mouse-event/constant';
import { restorePlaceHolderParaEndProps } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/place-holder-component/utils';
import set from 'lodash/set';
import { InteractionEvent } from '@tencent/alloy-renderer';
import { CANVAS_PATH_RENDER_ID } from '../../../core/id-constants';
import { enumerateGroup, enumerateSimilarGroup } from '@src/slide/domain/ui-model/element-modeles/property/group-operation/enumerate';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { isIframeShapeModel } from '@src/slide/domain/ui-model/elements/element-judge';
import { IMediaManager } from '@src/slide/application/service/media-manager/media-manager.interface';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { isSameObj } from '@src/js/utils';
import { TEXT_CHANGE_EVENT } from '@src/slide/application/service/text-ruler-manager/core/event-const';
import { ESpecialOperationType } from '@src/slide/application/service/selection/selected-with-commit-manager.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { isDecoratorKey } from '@src/slide/infrastructure/module/independent/keyboard/decorator';

export class ShapeEditLogic extends CommonEditLogicWithTextEditor {
    public selectedListener: IReactionDisposer;
    public iframePosterListener: IReactionDisposer;
    public mediaManager: IMediaManager | undefined;
    public setResize: boolean;
    declare protected factoryOptions: InterfaceFactoryOptions<IShapeModel>;
    private selectedOff: IReactionDisposer;
    private selectElementChangeReaction: IReactionDisposer;
    private previewReaction: IReactionDisposer;
    private resizingReaction: IReactionDisposer;
    private controlPointReaction: IReactionDisposer;
    private isEditorFocus = false;
    private autoFocus: boolean; // 移动端IOS无法自动获取焦点，但会改变editing状态
    private debouncedInnerMouseMove; // debounce mousemove 函数，解决鼠标移入 iframe 点击时可能误触发 mousemove 的问题
    private size: any;

    public constructor(factoryOptions: InterfaceFactoryOptions<IShapeModel>, component: ComponentFactory) {
        super(factoryOptions, component);
        // 事件绑定
        this.mediaManager = factoryOptions.mediaManager;
        this.debouncedInnerMouseMove = debounce(this.handleInnerMouseMove, 300);
        this.component.inner.onMouseDown(this.handleInnerMousedown);
        this.component.inner.onMouseMove(this.debouncedInnerMouseMove);
        this.component.inner.onMouseUp(this.handleInnerMouseUp);
        this.component.inner.onMouseLeave(this.handleInnerMouseLeave);
        this.component.inner.onDoubleClick(this.handleInnerDoubleClick);
        this.component.inner.onClick(this.handleInnerClick);

        const { model } = this.factoryOptions;
        this.addAllObservers();
        this.autoFocus = !browser.isMobile;

        if (isIframeShapeModel(model)) {
            this.addSelectedListener();
            this.addIframePosterListener();
        }

        const { width, height } = model.props.style;
        this.size = {
            width,
            height,
        };
    }

    // 临时方案，后期删除。
    public init(): void {
        super.init();
        this.component.inner.renderEditor();
        this.updateElementFocus();
    }

    /**
     * 由inner组件触发
     */
    public dispose(): void {
        this.removeAllObservers();
    }

    /**
     * 获取文本的参数以及回调
     */
    public getEditorProps = (): IEditorProps => {
        const { isEditable } = this.factoryOptions;
        if (!isEditable) {
            return {
                onMouseDown: this.handleEditorMouseDown,
            };
        }
        return this.getEditableProps();
    };

    /**
     * placeHolder点击事件回调
     * @param type
     * @param e
     */
    public handlePlaceHolderClick = (type: string, e): void => {
        switch (type) {
            case PlaceHolderMode.TXT:
            case PlaceHolderMode.BLANK:
                // 按住装饰键的话表示是选中，就不进入编辑态
                if (!isDecoratorKey(e)) {
                    this.enterEditor(e, 'placeholder');
                }
                break;
        }
    };

    /**
     * 点击Editor回调接口
     * 因为子类要访问父类里handleEditorMouseDown，所以该函数是public，这里override也要是public
     * @param e
     */
    public handleEditorMouseDown = (e): void => {
        super.handleEditorMouseDown(e);
        if (this.component.inner.is3DShape()) {
            this.component.inner.updateComponent();
        }
        const { model } = this.factoryOptions;
        const { selectedArray } = this.getSelectionService().selectedElements;
        // 两种情况需要更新选中元素
        // 1. 当前形状没选中
        // 2. 当前形状选中了但是还有其他形状在选中状态
        if (!model?.selected || selectedArray?.length !== 1) {
            this.makeElementSelected(e);
        }
    };

    public renderSearchHighlight({ searchResult, highlightResult, keyword }): void {
        this.component.inner.textEditor?.addSearchMark(searchResult, keyword);
        if (highlightResult) {
            this.component.inner.textEditor?.addHighlight([highlightResult], keyword);
        }
    }

    public setResizeValue = (b) => {
        this.component.inner.setShouldRender3D(!b);
        this.setResize = b;
    };

    public updateResizevalue = () => {
        const { model } = this.factoryOptions;
        const { width, height, controlValues } = model.props.style;

        if (this.size.width !== width || this.size.height !== height) {
            this.size.width = width;
            this.size.height = height;
            this.setResizeValue(true);
            model.pathParser.update({
                ...model.pathParser.model,
                width,
                height,
            });
        }

        const nextControlValues = (controlValues?.length ?? 0) > 0 ? controlValues
            : model.pathParser.getDefaultControlValues();
        if (!isSameObj(nextControlValues, model.pathParser.controlValues)) {
            model.pathParser.updateControlValues(nextControlValues);
            this.setResizeValue(false);
        }

        if (model.undoRedo) {
            this.setResizeValue(true);
        }
    };

    /**
     * 是否可以进入 readyEditing 状态
     * - 当前 shape 在编辑区
     * - 当前 shape 是唯一被选中的
     * @returns
     */
    public ifCanSetReadyEditing(): boolean {
        const { model, fromScale } = this.factoryOptions;
        const { selectedArray } = this.getSelectionService().selectedElements;
        return !fromScale && selectedArray?.length === 1 && model.selected;
    }

    private addAllObservers() {
        if (this.factoryOptions.fromScale === 0) {  // 注意： 编辑页才执行
            const { model } = this.factoryOptions;
            this.selectedOff = reaction(() => ({
                selected: this.factoryOptions.model?.selected,
            }), (obj) => {
                if (obj.selected) {
                    // 聚焦
                    this.handleElementSelected();
                } else {
                    // 失焦
                    this.handleElementUnSelected();
                    this.checkIfNeedDeleteText();
                }
            });
            this.selectElementChangeReaction = reaction(
                () => ({
                    selectedArray: this.getSelectionService().selectedElements.selectedArray,
                })
                , (obj) => {
                    // @ts-ignore
                    if (obj.selectedArray?.length > 1 && model.selected
                        && (this.textEditor?.isEditing || this.textEditor?.isReadyEditing)
                    ) {
                        // 多个形状被选中时，退出当前编辑态
                        this.textEditor?.triggerBlur();
                    }
                    this.handleSelectedArrayChange();
                },
            );
            this.resizingReaction = reaction(
                () => this.getAppService().viewableServiceLayer.editingStateManager.isResizing,
                (isResizing) => {
                    this.component.inner.setShouldRender3D(!isResizing);
                    if (!isResizing) {
                        this.setResize = true;
                    }
                },
            );
            this.controlPointReaction = reaction(
                () => this.factoryOptions.model.operatingGraphicInfo,
                (info) => {
                    this.component.inner.setShouldRender3D(!info);
                },
            );
        }
        this.addPreviewObserver();
    }

    private handleSelectedArrayChange() {
        const canSetReadyEditing = this.ifCanSetReadyEditing();
        const { inner } = this.component;
        if (canSetReadyEditing && inner.isPlaceHolderShow && inner.isPlaceholderTextEditable) {
            // 选中了 placeholder，渲染一个临时的 textEditor 用于直接输入
            inner.renderTempEditor();
        } else {
            // 当存在临时的 textEditor时，需删除
            if (inner.showTempTextEditor) {
                inner.clearTempEditor();
            }
        }
        // 如果当前文本框已经在编辑态，就不用主动进入 readyEditing
        if (canSetReadyEditing && this.textEditor && !this.textEditor?.isEditing) {
            this.textEditor.setReadyEditing();
        }
    }

    private removeAllObservers() {
        this.selectedOff?.();
        this.selectElementChangeReaction?.();
        this.removePreviewObserver();
        this.resizingReaction?.();
        this.controlPointReaction?.();
    }

    private addPreviewObserver() {
        if (!this.factoryOptions.fromScale) {
            this.previewReaction = reaction(() => {
                const { model } = this.factoryOptions;
                return model.selected
                    && ((this.getAppService() as IPcEditorAppServices)
                        .viewableServiceLayer?.getEditorAnimPreviewManager()?.isAnimPreviewing
                        || this.getAppService().editableServiceLayer?.previewService.isPreview);
            }, (isPreview) => {
                if (isPreview) {
                    // 进入预览态时，让当前元素失焦
                    this.handleElementUnSelected();
                }
            });
        }
    }

    private removePreviewObserver() {
        this.previewReaction?.();
    }

    private showNotEditTips = (cb?) => {
        if (this.getUserPriviledgeService().pptEditorState > PPTEditorStateType.EDITABLE) {
            this.getAppService().viewableServiceLayer.tipsManager.showCannotEditTips();
            cb?.();
        }
    };

    private handleInnerMouseMove = (e) => {
        const { model } = this.factoryOptions;

        // iframe 内鼠标移动时，若当前或其它 iframe 元素未被选中，重制选中状态
        // 有 iframe 元素被选中时不能重置，否则当鼠标移动到其它 iframe 元素未点击
        // 之前选中的 iframe 元素状态会被重置
        if (isIframeShapeModel(model) && !this.mediaManager?.isShowIframe) {
            this.mediaManager?.resetIframeSelectedState();

            // 若当前元素已选中，前置状态设为 true
            if (this.factoryOptions.model.selected) {
                this.mediaManager?.updateIframeSelectState(true);
            }
        }
    };

    private handleInnerMouseUp = (e) => {
        const { model } = this.factoryOptions;

        // 右键不唤起 iframe
        if (e.nativeEvent.button === MOUSE_EVENT_KEY.RIGHT) {
            return;
        }

        // 鼠标左键抬起后，更新后置状态
        if (isIframeShapeModel(model)) {
            this.mediaManager?.updateIframeSelectState(true);
        }
    };

    private handleInnerMouseLeave = () => {
        const { model } = this.factoryOptions;

        // 鼠标移出 iframe 元素后，取消 deboucne，移动到新的 iframe 元素后再触发
        if (isIframeShapeModel(model)) {
            this.debouncedInnerMouseMove.cancel();
        }
    };

    private handleInnerClick = (event: InteractionEvent) => {
        if (event.target?.id.includes(CANVAS_PATH_RENDER_ID) && this.ifCanSetReadyEditing()) {
            this.textEditor?.setReadyEditing();
        }
    };

    private handleInnerDoubleClick = (e) => {
        if (this.factoryOptions.isEditable) {
            e?.stopPropagation();
            if (!this.textEditor?.isEditing) {
                this.enterEditor(e);
            }
        }
        this.showNotEditTips(() => {
            e?.stopPropagation();
        });
    };

    private handleInnerMousedown = (event: InteractionEvent) => {
        // 阻止默认事件，否则隐藏输入框会失焦
        event.preventDefault();
    };

    private handleElementSelected() {
    }

    private handleElementUnSelected() {
        // 触发失焦逻辑，并更新数据
        this.textEditor?.triggerBlur();
    }

    /**
     * 获取当前shape中的editor组件
     */
    private get textEditor(): ITextEditor | null {
        return this.component.inner.textEditor;
    }

    /**
     * 获取可编辑editor的props
     */
    private getEditableProps = (): IEditorProps => {
        const { model, fromScale, modelParams, anim } = this.factoryOptions;
        // 缩略图默认更新，编辑区根据changeSet判断是否需要更新
        const shouldUpdate = !fromScale ? modelParams?.updateState?.editorShouldUpdate : true;
        return {
            shouldUpdate: (!!anim?.needUpdateFont) || shouldUpdate,
            cursor: this.getComputedCursor(),
            selected: model.selected,
            editable: true,
            levelChangeLock: this.getLevelChangeLock(),
            onChange: this.handleChange,
            onBeforeValueChange: this.beforeEditorValueChange,
            handleSelectionChange: this.handleSelectionChange,
            onTextChange: this.handleTextChange, // 处理输入时形状变化
            onStyleChange: this.handleEditorStyleChange,
            onEnterEditing: this.handleEnterEditing,
            onExitEditing: this.handleExitEditing,
            resetShouldUpdate: () => {
                if (!fromScale) set(modelParams, 'updateState.editorShouldUpdate', false); // 不让左侧列表影响右侧逻辑
            },
            onMouseUp: this.handleEditorMouseUp,
            onMouseDown: this.handleEditorMouseDown,
        };
    };

    /**
     * 数据变更，触发上传
     * @param textBodyInfo
     */
    private handleChange = (textBodyInfo) => {
        transaction(() => {
            const { paragraphList, textBodyStyle } = textBodyInfo;
            const { model } = this.factoryOptions;
            let changedParagraphList = paragraphList;
            if (model.isPlaceholder) {  // place holder还原ndParaRPr
                changedParagraphList = restorePlaceHolderParaEndProps(model, changedParagraphList);
            }
            if (model.isPlFromMasterOrLayout) {
                const type = getPlaceHolderType(model);
                // 占位符文字是否自定义
                const isTextCustom = !judgeTextIsEmpty(changedParagraphList)
                    && !isTextSomeAsDefault(changedParagraphList, type);
                model.updateMasterAndLayoutPlaceHolder(changedParagraphList, textBodyStyle, isTextCustom);
            } else {
                model?.updateParagraphList(changedParagraphList, textBodyStyle);
            }
        });
    };

    private beforeEditorValueChange = () => {
        const { textSelectionUndoRedo, model } = this.factoryOptions;
        // 文字有值变更，清空暂存的选区 redo 栈
        textSelectionUndoRedo?.clearRedo();
        // 暂存 editor 数据变更前的文字选区
        textSelectionUndoRedo?.stashSelectionBefore(this.textEditor!.getSelection(), model.id);
    };
    private handleSelectionChange = () => {
        const { textSelectionUndoRedo, model } = this.factoryOptions;
        // 暂存当前的文字选区，用于选区的 undo redo
        this.textEditor && textSelectionUndoRedo?.stashSelectionAfter(this.textEditor.getSelection(), model.id);
    };
    /**
     * 文字输入变更
     * @param height
     * @param width
     */
    private handleTextChange = ({ height, width }) => {
        if (this.component.inner.is3DShape()) {
            this.component.inner.updateComponent();
        }
        const { model } = this.factoryOptions;

        // 如果当前渲染了 placeholder，说明此时是 textEditor 和 placeholder 共存的状态
        // 此时 textEditor 从 readEditing 状态变为了 isEditing，需要移除掉 placeholder
        if (this.component.inner.isRenderPlaceholderComponent) {
            this.component.inner.renderEditor();
        }
        // 触发锁页
        this.getAppService().viewableServiceLayer.textEditorStateService.editorContentChangeFlag += 1;
        // 输入过程中超出画布范围，需要重新计算画布大小
        this.overCanvasSize();
        window.dispatchEvent(new Event(TEXT_CHANGE_EVENT));
        if (model.textBodyStyle?.autoFitType !== AutoFitType.SHAPE_AUTO_FIT) {
            return;
        }
        const textDirection = model.textBodyStyle?.wrapperStyle?.textDirection;
        const { displayInlineSize, displayBlockSize } = this.getDisplayStyle(height, width, textDirection);
        TextAdjustManager.getInstance().updateShapeStyleForUI(model, displayBlockSize, displayInlineSize);
        this.getBorderSketchManager()?.updatePathListUIWhenSizeChange(
            this.factoryOptions.model,
            {
                width: this.factoryOptions.model.props.style.width,
                height: this.factoryOptions.model.props.style.height,
            },
        );
    };

    // 输入过程中超出画布范围，需要重新计算画布大小
    private overCanvasSize = () => {
        const { app, worldBounds } = this.component.wrapper;
        const { x, y, width, height } = worldBounds;
        if (app && (app.width < x + width || app.height < y + height)) {
            window.dispatchEvent(new Event('immediately-resize'));
        }
    };

    /**
     * 文本样式发生变更。
     * @param style
     */
    private handleEditorStyleChange = (style) => {
        const { model, fromScale } = this.factoryOptions;
        if (!fromScale && model) {
            model.selectionStyles = style.style;
        }
    };

    private enterEditor = (e: InteractionEvent, source?: string) => {
        this.isEditorFocus = true;
        this.makeElementSelected(e, source === 'placeholder');
        this.component.inner.renderEditor();
        this.textEditor?.setFocus(undefined, e);
    };

    private handleExitEditing = () => {
        if (this.component.inner.wordArtComponent) {
            this.component.inner.wordArtComponent.onExitEditing();
        }
        if (this.component.inner.is3DShape()) {
            this.component.inner.setShouldRender3D(true);
            this.component.inner.updateComponent();
        }
        if (this.isEditorFocus) {
            const { inner } = this.component;
            this.isEditorFocus = false;
            inner.renderEditor();
            // 更新完 editor 之后，如果渲染了占位符没有渲染 textEditor，说明是从编辑器失焦切换为了占位符
            // 如果满足条件的话需要进入 resetReadyEditing 状态
            if (
                inner.isRenderPlaceholderComponent
                && inner.isPlaceholderTextEditable
                && !this.textEditor
                && this.ifCanSetReadyEditing()
            ) {
                inner.renderTempEditor();
                if (this.textEditor) {
                    (this.textEditor as ITextEditor).setReadyEditing();
                }
            }
        }

        // 广播编辑态事件
        window.dispatchEvent(new Event('exitEditing'));
    };

    private handleEnterEditing = () => {
        if (this.component.inner.wordArtComponent) {
            this.component.inner.wordArtComponent.onEnterEditing();
        }
        if (this.component.inner.is3DShape()) {
            this.component.inner.setShouldRender3D(false);
            this.component.inner.updateComponent();
        }
        // 进入编辑态时，如果 placeholder 还在渲染状态，需要重新渲染下 editor 移除 placeholder
        if (this.component.inner.isRenderPlaceholderComponent) {
            this.component.inner.renderEditor();
        }

        // 广播编辑态事件
        window.dispatchEvent(new Event('enterEditing'));
    };

    private updateElementFocus = () => {
        if (this.autoFocus) {
            requestAnimationFrame(this.initFocus);
        }
    };

    // 如果shape.init_focus为true，默认设置焦点到text-editor上
    private initFocus = () => {
        const { model, fromScale } = this.factoryOptions;
        if (model?.initFocus && !fromScale) {
            this.triggerFocus();
            if (model?.initFocus) {
                model.initFocus = false;
            }
        }
    };

    private triggerFocus = () => {
        const { isEditable } = this.factoryOptions;
        if (isEditable) {
            this.textEditor?.setFocus();
        }
        this.showNotEditTips();
    };

    private getDisplayStyle(height, width, textDirection?) {
        let inlineSize;
        let blockSize;
        const { model } = this.factoryOptions;
        const isVert = isVerticalDirection(textDirection);
        const logicKeys = getLogicPropertyKeyMap(textDirection);
        if (isVert) {
            inlineSize = height;
            blockSize = width;
        } else {
            inlineSize = width;
            blockSize = height;
        }

        const textRect = getTextRect(model, model.pathParser, model.props.style);
        const displayBlockSize = blockSize;
        let displayInlineSize;
        if (inlineSize) {
            displayInlineSize = TextAdjustManager.getInstance().
                getDisplayInlineSize(model, inlineSize, textRect, null, textDirection);
        } else {
            displayInlineSize = model.props.style[logicKeys.inlineSize];
        }
        return { displayInlineSize, displayBlockSize };
    }

    // 空文本在失去焦点时删除
    private checkIfNeedDeleteText = () => {
        const { model } = this.factoryOptions;
        if (model?.currentEditState !== PPTEditorStateType.EDITABLE) {
            // 无编辑权限
            return;
        }
        if (model.props.subType !== 1 || model.selected) {
            // 不为文本框 || 当前选中
            return;
        }
        if (!isEmptyText(model)) {
            // 非空文本
            return;
        }
        this.getSelectionService()?.selectedWithCommit?.markSpecialOperate(ESpecialOperationType.keep);
        model.soruceRefForUI?.deleteElement([model]);
    };

    /**
     * 触发元素选中
     * @param e
     * @param notReport
     */
    private makeElementSelected = (e: any, notReport?: boolean) => {
        const component = this.factoryOptions.model;
        (this.getAppService() as IPcEditorAppServices).editableServiceLayer?.selectedAnims.clear();
        let rootGroup: any = null;
        enumerateSimilarGroup(component, (groupModel) => {
            rootGroup = groupModel;
        });

        const selectedSlideArr = this.getSelectionService()
            .selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);

        mobxTransaction(() => {
            if (isDecoratorKey(e)) {
                // 选择 slide 的上的文本编辑器 就把组合中选择元素去除
                if (rootGroup === null) {
                    this.addSelectElement();
                } else if (rootGroup.selected) {
                    this.addWithElementSelected(selectedSlideArr, rootGroup);
                } else {
                    this.clearAndAddGroupSelected(rootGroup);
                }
            } else {
                this.setElementSelected(rootGroup, notReport);
            }
        });
    };


    private addSelectElement = () => {
        const component = this.factoryOptions.model;
        this.getSelectionService().selectedElements.clear(SelectedEleMgrType.GROUP);
        if (component.selectable && !component.selected) {
            this.getSelectionService().selectedElements.add(component);
        }
        this.triggerEditorBlur();
    };

    private addWithElementSelected = (selectedSlideArr, rootGroup) => {
        const component = this.factoryOptions.model;
        if (selectedSlideArr.length === 1) {
            if (component?.selectable && !component?.selected) {
                this.getSelectionService().selectedElements.add(component);
            }
            this.triggerEditorBlur();
        } else {
            this.getSelectionService().selectedElements.remove(rootGroup);
            this.triggerEditorBlur();
        }
    };

    private clearAndAddGroupSelected = (rootGroup) => {
        this.getSelectionService().selectedElements.clear(SelectedEleMgrType.GROUP);
        // 添加Root Group 逻辑
        const selectedArr = this.getSelectionService()
            .selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        if (rootGroup && selectedArr.indexOf(rootGroup) === -1) {
            this.getSelectionService().selectedElements.add(rootGroup);
        }
        this.triggerEditorBlur();
    };

    private triggerEditorBlur = () => {
        this.textEditor?.triggerBlur();
    };

    private setElementSelected = (rootGroup: any, notReport?: boolean) => {
        const component = this.factoryOptions.model;
        const lastSelected = component?.selected;
        this.getSelectionService().selectedElements.clear();
        this.getSelectionService().selectedElements.add(component);
        // 添加Root Group 逻辑
        const selectedArr = this.getSelectionService()
            .selectedElements.selectedArrayForType(SelectedEleMgrType.SLIDE);
        if (rootGroup && selectedArr.indexOf(rootGroup) === -1) {
            this.getSelectionService().selectedElements.add(rootGroup);
        }

        if (component?.selectable && !lastSelected && !notReport) {
            let inGroup = false;
            enumerateSimilarGroup(component, () => {
                inGroup = true;
                return true;
            });
            if (!inGroup) {
                tdw({
                    opername: TDWOperationType.DOC_PPT,
                    module: TDWModuleType.PC_OBJ,
                    action: TDWActionType.CLK_EDIT,
                });
            }
        }
    };

    private addSelectedListener = () => {
        const { model } = this.factoryOptions;

        if (model.selected) {
            // 当 iframe 刚创建时自动为选中状态，不会调用下面的 reaction
            this.handleModelSelected();
        }

        this.selectedListener = reaction(() => model.selected, (selected: boolean) => {
            if (selected) {
                this.handleModelSelected();
            } else {
                this.handleModelUnSelected();
            }
        });
    };

    private handleModelSelected = () => {
        this.mediaManager?.updateElementKey(this.factoryOptions.model.key);
        this.mediaManager?.resetIframeSelectedState();
        this.mediaManager?.updateIframeSelectState(true);
    };

    private handleModelUnSelected = () => {
        this.mediaManager?.resetIframeSelectedState();
        this.mediaManager?.updateElementKey('');
    };

    private addIframePosterListener = () => {
        const { model } = this.factoryOptions;

        this.iframePosterListener = when(
            () => !isUndefined(this.mediaManager?.iframePosterCaches.get(model.key)),
            () => {
                this.component.updateComponent();
            },
        );
    };

    private getLevelChangeLock = () => {
        const { model } = this.factoryOptions;
        if (!model.isPlaceholder) return false;
        const type = model.placeHolder?.type;
        return type === PlaceHolderMode.TITLE || type === PlaceHolderMode.CTRTITLE;
    };

    private getBorderSketchManager() {
        const appServices = AppServiceProvider.getInstance().getAppServices() as IEditorAppServices;
        return appServices.editableServiceLayer?.borderSketchManager;
    }
}
